<template>
  <UIForm
    id="modal-publish-to-web-form"
    class="ModalPublishToWeb__form"
    @submit.prevent="onFormSubmit"
  >
    <UIModalContent>
      <div class="ModalPublishToWeb__body">
        <UIBanner
          v-if="willReplacePublished"
          theme="warning"
          icon="triangleExclamationRegular"
          titleTag="h3"
          title="Only one published webpage is allowed per conversation."
          description="Publishing this version will replace the currently published one."
        />

        <p v-if="showIntro" class="ModalPublishToWeb__intro">
          This asset will be hosted by EverGPT and shared through a secure URL.
          You control who can access it and can remove it anytime.
        </p>

        <UIInputText
          id="websiteName"
          class="ModalPublishToWeb__field"
          name="websiteName"
          isSanitised
          label="Name your asset"
          placeholder="Enter a title"
          :isDisabled="isPublishing"
        />

        <div
          class="ModalPublishToWeb__slug-field"
          :data-slug-status="slugStatus"
        >
          <UIInputText
            id="websiteSlug"
            class="ModalPublishToWeb__field"
            name="websiteSlug"
            isSanitised
            label="Custom URL"
            placeholder="page-slug"
            prefixText="/w/"
            :isDisabled="isPublishing"
            spellcheck="false"
            autocomplete="off"
            @blur="onSlugBlur"
          />

          <!-- The slug check reports itself inside the field rather than as hint
               text — a spinner while it's in flight, then the tick in the same
               spot — so the hint line is left to report problems only. The
               positioned wrapper is ours because UITooltip renders a fragment
               (anchor + floating panel), which a parent's scoped style can't
               reach. -->
          <span
            v-if="slugStatusIndicator"
            class="ModalPublishToWeb__slug-status"
          >
            <UITooltip placement="top">
              <template #trigger>
                <UISpinner
                  v-if="slugStatusIndicator === 'checking'"
                  size="sm"
                  class="ModalPublishToWeb__slug-spinner"
                  role="status"
                  :aria-label="slugStatusLabel"
                />
                <UIIcon
                  v-else
                  name="checkRegular"
                  class="ModalPublishToWeb__slug-available-icon"
                  role="img"
                  :aria-label="slugStatusLabel"
                />
              </template>

              <template #content>{{ slugStatusLabel }}</template>
            </UITooltip>
          </span>
        </div>

        <DropdownWebsiteVisibility
          :modelValue="visibilityMode"
          :isDisabled="isPublishing"
          :allowPassword="allowPasswordVisibility"
          @update:modelValue="setVisibilityMode"
        />

        <UIBanner
          v-if="willRemovePassword"
          theme="warning"
          icon="triangleExclamationRegular"
          titleTag="h3"
          title="Password protection will be removed."
          description="To re-add protection, you'll need to set a new password."
        />

        <WebsiteShareUserSection
          v-if="visibilityMode === 'private'"
          :shareUsers="shareUsers"
          :usersWithAccess="usersWithAccess"
          @update:shareUsers="setShareUsers"
        />

        <UIInputText
          v-if="visibilityMode === 'password' && allowPasswordVisibility"
          id="websitePassword"
          class="ModalPublishToWeb__field"
          name="websitePassword"
          :label="passwordFieldLabel"
          :type="isPasswordVisible ? 'text' : 'password'"
          :placeholder="passwordPlaceholder"
          :isDisabled="isPublishing"
          :trailIcon="isPasswordVisible ? 'eyeSlashRegular' : 'eyeRegular'"
          @clicktrailIcon="togglePasswordVisibility"
        />
      </div>
    </UIModalContent>

    <UIModalActions>
      <UIButton
        type="button"
        theme="secondary"
        :isDisabled="isPublishing"
        @click="emit('close')"
      >
        Cancel
      </UIButton>
      <UIButton
        type="submit"
        :isDisabled="!canSubmit || isPublishing"
        :isLoading="isPublishing"
      >
        {{ submitButtonLabel ?? (isUpdate ? 'Update now' : 'Publish Now') }}
      </UIButton>
    </UIModalActions>
  </UIForm>
</template>

<script lang="ts" setup>
import { computed, inject, ref, watch } from 'vue'

import {
  UIBanner,
  UIButton,
  UIForm,
  UIIcon,
  UIInputText,
  UIModalActions,
  UIModalContent,
  UISpinner,
  UITooltip,
} from '@foundever/ui-lib'
import { useDebounceFn } from '@vueuse/core'
import { useForm } from 'vee-validate'
import { array, object, string } from 'yup'

import type { ISelectedUser } from '@/apiClient/types/user'
import { useHtmlPreviewPublish } from '@/composables/useHtmlPreviewPublish'
import { isPosthogFlagOn, PosthogFeatureFlags } from '@/lib/posthog'
import { isValidWebsiteSlug } from '@/utils/websiteSlug'
import { websitePublishKey } from '@/views/components/preview/websitePublishKey'

import DropdownWebsiteVisibility from './DropdownWebsiteVisibility.vue'
import WebsiteShareUserSection from './WebsiteShareUserSection.vue'

import type { PublishToWebFormValues, WebsiteVisibilityMode } from '../types'

const props = withDefaults(
  defineProps<{
    isOpen: boolean
    isPublishing: boolean
    isUpdate: boolean
    hadPassword: boolean
    /** Chat already has a published webpage and this isn't it — publishing will
     * replace the currently published one (one webpage per conversation). */
    willReplacePublished?: boolean
    usersWithAccess: ISelectedUser[]
    initialValues: PublishToWebFormValues
    websiteId?: string | null
    ownedSlug?: string | null
    showIntro?: boolean
    submitButtonLabel?: string
  }>(),
  {
    showIntro: true,
    willReplacePublished: false,
  },
)

const emit = defineEmits<{
  submit: [values: PublishToWebFormValues]
  close: []
}>()

const websitePublish = inject(websitePublishKey)
if (!websitePublish) {
  throw new Error('websitePublishKey is not provided')
}
const { slugStatus, checkSlugAvailability } = websitePublish
const htmlPreview = useHtmlPreviewPublish()

/** Read inside the validation schema, which runs outside this setup's reactive scope. */
const isSlugTaken = computed(() => slugStatus.value === 'taken')

/**
 * Password protection is flagged off/on globally. With the flag off the option is
 * dropped from the visibility dropdown, so no page can be *put* behind a password —
 * except one that already has one, which keeps the option so editing its settings
 * shows (and can move off) its real protection rather than silently misreporting it.
 */
const allowPasswordVisibility = computed(
  () =>
    isPosthogFlagOn(PosthogFeatureFlags.WebPublishingPassword) ||
    props.hadPassword,
)

const validationSchema = object({
  websiteName: string().trim().required('Name is required'),
  websiteSlug: string()
    .trim()
    .required('Custom URL is required')
    .test(
      'slug-format',
      'Use lowercase letters, numbers, and hyphens (3–200 chars)',
      (value) => isValidWebsiteSlug(value ?? ''),
    )
    // A taken slug reports through the field's own error line rather than a
    // second message of its own, so the form shows one problem in one place.
    .test('slug-taken', 'Slug is already taken', () => !isSlugTaken.value),
  websitePassword: string().when('visibilityMode', {
    is: 'password',
    then: (schema) => {
      // The field is hidden when the option isn't available, so requiring it would
      // leave the form unsubmittable with nothing on screen to fix.
      if (!allowPasswordVisibility.value) return schema
      // Map blank → undefined so `.min` treats it as absent (skips). In update
      // mode a blank field means "keep the current password", so it stays
      // optional; otherwise a password is required.
      const withLength = schema
        .transform((value) => value?.trim() || undefined)
        .min(8, 'Password must be at least 8 characters')
        .max(72, 'Password must be at most 72 characters')
      return props.isUpdate && props.hadPassword
        ? withLength
        : withLength.required('Password is required')
    },
  }),
  visibilityMode: string().oneOf(['foundever', 'private', 'password']),
  shareUsers: array(),
})

const { handleSubmit, resetForm, values, meta, setFieldValue, validateField } =
  useForm<PublishToWebFormValues>({
    validationSchema,
    initialValues: props.initialValues,
  })

const visibilityMode = computed(() => values.visibilityMode)
const shareUsers = computed(() => values.shareUsers)

const isPasswordVisible = ref(false)

function togglePasswordVisibility() {
  isPasswordVisible.value = !isPasswordVisible.value
}

const slugCheckOptions = computed(() => ({
  websiteId: props.websiteId ?? htmlPreview.websiteId.value,
  ownedSlug: props.ownedSlug ?? htmlPreview.websiteOwnedSlug.value,
}))

/** Which in-field indicator the slug check is currently showing, if any. */
const slugStatusIndicator = computed(() =>
  slugStatus.value === 'checking' || slugStatus.value === 'available'
    ? slugStatus.value
    : null,
)

const slugStatusLabel = computed(() =>
  slugStatusIndicator.value === 'checking'
    ? 'Checking availability…'
    : 'URL available',
)

const passwordFieldLabel = computed(() =>
  props.hadPassword && props.isUpdate ? 'New password' : 'Password',
)

const passwordPlaceholder = computed(() =>
  props.hadPassword && props.isUpdate
    ? 'Leave blank to keep the current password'
    : 'Enter a password',
)

/**
 * Moving a password-protected page onto any other visibility sends
 * `remove_password`, dropping the stored password for good. Keyed on the *saved*
 * protection (`hadPassword`), like the "keep the current password" placeholder —
 * a page that never had one has nothing to lose.
 */
const willRemovePassword = computed(
  () =>
    props.hadPassword && props.isUpdate && visibilityMode.value !== 'password',
)

const canSubmit = computed(
  () =>
    meta.value.valid &&
    slugStatus.value !== 'taken' &&
    slugStatus.value !== 'checking' &&
    slugStatus.value !== 'invalid',
)

const debouncedCheckSlug = useDebounceFn((slug: string) => {
  void checkSlugAvailability(slug, slugCheckOptions.value)
}, 400)

watch(
  () => props.isOpen,
  (open) => {
    if (!open) return
    // Always start masked so reopening the modal never reveals a password.
    isPasswordVisible.value = false
    resetForm({ values: props.initialValues })
    // Verify the prefilled slug immediately (not via the 400ms debounce): the
    // shared `slugStatus` can otherwise linger at a stale `available`/`taken`
    // value from a previous open, or stay `idle`, letting `canSubmit` pass with
    // an unverified slug during the debounce window.
    void checkSlugAvailability(values.websiteSlug, slugCheckOptions.value)
  },
)

watch(
  () => props.initialValues,
  (nextValues) => {
    if (!props.isOpen) return
    resetForm({ values: nextValues })
  },
  { deep: true },
)

watch(
  () => values.websiteSlug,
  (slug) => {
    if (!slug) {
      slugStatus.value = 'invalid'
      return
    }
    debouncedCheckSlug(slug)
  },
)

// The availability verdict lands asynchronously, without the slug itself
// changing — so re-run the field's validation to surface (or clear) the
// `slug-taken` error at that point.
watch(isSlugTaken, () => {
  void validateField('websiteSlug')
})

function setVisibilityMode(mode: WebsiteVisibilityMode) {
  setFieldValue('visibilityMode', mode)
}

function setShareUsers(users: ISelectedUser[]) {
  setFieldValue('shareUsers', users)
}

function onSlugBlur() {
  void checkSlugAvailability(values.websiteSlug, slugCheckOptions.value)
}

const onFormSubmit = handleSubmit((formValues) => {
  emit('submit', formValues)
})
</script>

<style scoped lang="scss">
.ModalPublishToWeb {
  .ModalPublishToWeb__form {
    gap: 0;
  }

  .ModalPublishToWeb__intro {
    margin: 0;
    @include font-body-small-regular;
    color: var(--text-primary, #12123f);
  }

  .ModalPublishToWeb__slug-field {
    position: relative;

    // The indicator belongs inside the field box, which is the last element in this
    // wrapper and a fixed 1.875rem tall (ui-lib) — so anchor to the wrapper's bottom
    // and centre against that height. Fixed width too: the spinner (1rem, sized
    // inline by ui-lib) and the check (0.75rem) centre in the same slot, so the
    // hand-over from one to the other doesn't shift sideways.
    .ModalPublishToWeb__slug-status {
      display: flex;
      align-items: center;
      justify-content: center;
      position: absolute;
      right: var(---fds-global-space-sm);
      bottom: 0;
      width: 1rem;
      height: 1.875rem;

      // The tooltip's own trigger wrapper sits between this box and the
      // spinner/tick, so it has to fill the slot for them to stay centred.
      :deep(.UITooltip__trigger) {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;
      }
    }

    .ModalPublishToWeb__slug-available-icon {
      --size: 1.125rem;
      --color: #{$color-success-60};
    }

    // Keep a long slug from running under the indicator.
    &[data-slug-status='checking'] :deep(.UIInputTextBase__input),
    &[data-slug-status='available'] :deep(.UIInputTextBase__input) {
      padding-right: var(---fds-global-space-md);
    }
  }
}
</style>

<style>
.ModalPublishToWeb__body {
  display: flex;
  flex-direction: column;
  gap: var(---fds-global-space-md);
}
</style>
